import {
  HiCog,
  HiCollection,
  HiLink,
  HiChatAlt2,
  HiLogout,
  HiQuestionMarkCircle,
  HiUser,
  HiUserGroup,
} from "react-icons/hi";
import { MdVideoSettings } from "react-icons/md";
import { FaDiscord } from "react-icons/fa";

import { useUser } from "MVVM/Hooks/useUser";
import { useSubscriptionInfo } from "MVVM/Hooks/useHasProAccess";
import { ClipQuotaBar } from "MVVM/Components/ClipQuotaBar";
import { URLS } from "MVVM/Utilities/Maps";
import { DISCORD_INVITE_URL, SUPPORT_URL, profilePath } from "../links";
import { ReactComponent as HistoryLogo } from "MVVM/Assets/Nav/nav-history.svg";
import {
  MenuAction,
  MenuCount,
  MenuDivider,
  MenuIcon,
  MenuLabel,
  MenuLink,
  MenuPending,
  MenuList,
  PersonalRows,
  MenuPanel,
  MenuPlanFooter,
  MenuPlanCta,
  QuotaSlot,
} from "./styledComponents";
import { useActiveClips } from "./useActiveClips";

export const Panel = ({
  panelId,
  onNavigate,
  onOpenFeedback,
}: {
  panelId: string;
  onNavigate: () => void;
  onOpenFeedback: () => void;
}) => {
  const { allstarUser, logout } = useUser();
  const { isProOrHigher } = useSubscriptionInfo();

  const pendingClips = useActiveClips().length;

  const user = allstarUser?.user;
  const email = allstarUser?.decoded?.sub;

  return (
    <MenuPanel id={panelId} aria-label="Your Allstar">
      <QuotaSlot>
        <ClipQuotaBar clipQuota={user?.clipQuota} userEmail={email} compact />
      </QuotaSlot>

      <MenuList>
        <PersonalRows>
          <MenuLink
            to={profilePath(user?.profile?.username || user?.username)}
            onClick={onNavigate}
          >
            <MenuIcon>
              <HiUser />
            </MenuIcon>
            <MenuLabel>My Profile</MenuLabel>
          </MenuLink>

          <MenuLink to="/library" onClick={onNavigate}>
            <MenuIcon>
              <HiCollection />
            </MenuIcon>
            <MenuLabel>My Library</MenuLabel>
            {/* While anything is rendering the pending count is the more useful
                number, so it takes the slot rather than sitting beside the total. */}
            {pendingClips ? (
              <MenuPending>{pendingClips} rendering</MenuPending>
            ) : (
              Number(user?.stats?.totalClips) > 0 && (
                <MenuCount>{user?.stats?.totalClips}</MenuCount>
              )
            )}
          </MenuLink>

          <MenuLink to={URLS.MATCH_HISTORY} onClick={onNavigate}>
            <MenuIcon>
              <HistoryLogo />
            </MenuIcon>
            <MenuLabel>My Matches</MenuLabel>
          </MenuLink>
          <MenuLink to={URLS.TEAMS} onClick={onNavigate}>
            <MenuIcon>
              <HiUserGroup />
            </MenuIcon>
            <MenuLabel>My Teams</MenuLabel>
          </MenuLink>
        </PersonalRows>
      </MenuList>

      <MenuDivider />

      <MenuList>
        <MenuLink to="/accountsettings" onClick={onNavigate}>
          <MenuIcon>
            <HiCog />
          </MenuIcon>
          <MenuLabel>Account Settings</MenuLabel>
        </MenuLink>

        <MenuLink to={URLS.CLIP_SETTINGS} onClick={onNavigate}>
          <MenuIcon>
            <MdVideoSettings />
          </MenuIcon>
          <MenuLabel>Clip Settings</MenuLabel>
        </MenuLink>

        <MenuLink to="/connectedaccounts" onClick={onNavigate}>
          <MenuIcon>
            <HiLink />
          </MenuIcon>
          <MenuLabel>Connected Accounts</MenuLabel>
        </MenuLink>
      </MenuList>

      <MenuDivider />

      <MenuList>
        <MenuAction
          as="a"
          href={SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
        >
          <MenuIcon>
            <HiQuestionMarkCircle />
          </MenuIcon>
          <MenuLabel>Support</MenuLabel>
        </MenuAction>

        <MenuAction
          as="a"
          href={DISCORD_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
        >
          <MenuIcon>
            <FaDiscord />
          </MenuIcon>
          <MenuLabel>Join our Discord</MenuLabel>
        </MenuAction>

        <MenuAction type="button" onClick={onOpenFeedback}>
          <MenuIcon>
            <HiChatAlt2 />
          </MenuIcon>
          <MenuLabel>Leave Feedback</MenuLabel>
        </MenuAction>

        <MenuAction type="button" onClick={() => logout("Site nav user menu.")}>
          <MenuIcon>
            <HiLogout />
          </MenuIcon>
          <MenuLabel>Log out</MenuLabel>
        </MenuAction>
      </MenuList>

      {/* Free accounts had no upgrade route anywhere in the panel: the footer
          only rendered for subscribers, and the bar's trial button is hidden
          once you have used your trial. The identity panel spec puts an upgrade
          CTA here as state 2's most prominent element.

          Tier-agnostic wording on purpose. The subscription overhaul renamed
          the tiers to Basic, Standard and Unlimited, so the spec's "Upgrade to
          Pro" no longer names a plan we sell, and the pricing page does the
          selling anyway. */}
      <MenuPlanFooter>
        <MenuPlanCta
          to={isProOrHigher ? "/subscriptions" : URLS.UPGRADE}
          onClick={onNavigate}
        >
          {isProOrHigher ? "Manage plan" : "Upgrade your plan"}
        </MenuPlanCta>
      </MenuPlanFooter>
    </MenuPanel>
  );
};
